// Updated: 2026-08-20T21:10:00Z
// Purpose: Parse playback-buffer settings and calculate adaptive sentence scan-ahead windows.

export interface PlaybackBufferingSettings {
  dynamic: boolean;
  scanAheadSeconds: number;
}

export interface PlaybackBufferingTelemetry {
  cacheMissRatio: number;
  averageGenerationSeconds: number;
  averageClipSeconds: number;
}

export const MIN_SCAN_AHEAD_SECONDS = 5;
export const MAX_SCAN_AHEAD_SECONDS = 300;

export const DEFAULT_PLAYBACK_BUFFERING_SETTINGS: PlaybackBufferingSettings = {
  dynamic: true,
  scanAheadSeconds: 30,
};

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

export const parsePlaybackBufferingSettings = (
  value: unknown,
): PlaybackBufferingSettings => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return DEFAULT_PLAYBACK_BUFFERING_SETTINGS;
  }

  const record = value as Record<string, unknown>;
  const rawSeconds = Number(record.scan_ahead_seconds);
  const scanAheadSeconds = Number.isFinite(rawSeconds)
    ? clamp(Math.round(rawSeconds), MIN_SCAN_AHEAD_SECONDS, MAX_SCAN_AHEAD_SECONDS)
    : DEFAULT_PLAYBACK_BUFFERING_SETTINGS.scanAheadSeconds;

  return {
    dynamic:
      typeof record.dynamic === "boolean"
        ? record.dynamic
        : DEFAULT_PLAYBACK_BUFFERING_SETTINGS.dynamic,
    scanAheadSeconds,
  };
};

export const estimateSpeechDurationSeconds = (text: string): number => {
  const wordCount = text.trim().split(/\s+/u).filter(Boolean).length;
  if (wordCount === 0) return 0;

  // 150 words per minute plus a small boundary pause is a conservative preview estimate.
  return Math.max(0.75, wordCount / 2.5 + 0.2);
};

export const adaptiveScanAheadSeconds = (
  settings: PlaybackBufferingSettings,
  telemetry: PlaybackBufferingTelemetry,
): number => {
  const base = clamp(
    settings.scanAheadSeconds,
    MIN_SCAN_AHEAD_SECONDS,
    MAX_SCAN_AHEAD_SECONDS,
  );
  if (!settings.dynamic) return base;

  const missRatio = clamp(telemetry.cacheMissRatio, 0, 1);
  const averageClip = Math.max(0.75, telemetry.averageClipSeconds || 0.75);
  const generationRatio = Math.max(
    1,
    (telemetry.averageGenerationSeconds || averageClip) / averageClip,
  );
  const latencyMultiplier = Math.min(0.5, Math.max(0, generationRatio - 1) * 0.1);
  const demandMultiplier = Math.min(2, 1 + missRatio * 0.5 + latencyMultiplier);
  const latencyFloor = telemetry.averageGenerationSeconds > 0
    ? telemetry.averageGenerationSeconds * 2
    : base;

  return clamp(
    Math.ceil(Math.max(base * demandMultiplier, latencyFloor)),
    MIN_SCAN_AHEAD_SECONDS,
    MAX_SCAN_AHEAD_SECONDS,
  );
};

export const scanAheadIndexes = <T>(
  items: T[],
  startIndex: number,
  targetSeconds: number,
  durationForItem: (item: T) => number,
): number[] => {
  const indexes: number[] = [];
  let bufferedSeconds = 0;

  for (
    let index = Math.max(0, startIndex);
    index < items.length && (indexes.length === 0 || bufferedSeconds < targetSeconds);
    index += 1
  ) {
    indexes.push(index);
    bufferedSeconds += Math.max(0, durationForItem(items[index]));
  }

  return indexes;
};

// Start on the first ready sentence; look-ahead continues independently.
export const startupBufferIndexes = <T>(items: T[]): number[] => items.length ? [0] : [];
